import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { Alert } from "react-bootstrap";

const LocationList = ({ onEdit }) => {
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });
  const { tenant_id, user } = useContext(AuthContext);

  useEffect(() => {
    if (!tenant_id || !user?.user_id || !user?.role) return;

    const fetchLocations = async () => {
      try {
        const res = await secureAxios.get("/api/locations");
        setLocations(res.data);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setAlert({
          show: true,
          message: "Failed to load locations. Please refresh the page.",
          variant: "danger",
        });
      }
    };

    fetchLocations();
  }, [tenant_id, user?.user_id, user?.role]);

  const handleDelete = async (location_id) => {
    if (window.confirm("Are you sure you want to delete this location?")) {
      try {
        await secureAxios.delete(`/api/locations/${location_id}`);

        setLocations((prev) =>
          prev.filter((loc) => loc.location_id !== location_id)
        );

        setAlert({
          show: true,
          message: "✅ Location deleted successfully.",
          variant: "success",
        });
      } catch (err) {
        console.error("Error deleting location:", err);
        setAlert({
          show: true,
          message: "❌ Failed to delete location. Please try again.",
          variant: "danger",
        });
      }
    }
  };

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  if (!user) return <p>Loading user...</p>;

  const filteredLocations = locations.filter((loc) =>
    loc.location_name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div>
      <h5>📍 Saved Locations</h5>

      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="🔍 Filter by location name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter locations"
        />
      </div>

      {locations.length === 0 ? (
        <p>No locations found.</p>
      ) : (
        <>
          {/* Desktop/tablet view */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Entity Type</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((loc) => (
                  <tr key={loc.location_id}>
                    <td>{loc.location_name}</td>
                    <td>{loc.entity_type}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => onEdit(loc)}
                        aria-label={`Edit ${loc.location_name}`}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(loc.location_id)}
                        aria-label={`Delete ${loc.location_name}`}
                      >
                        🗑 Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile-friendly stacked list */}
          <ul className="list-group d-block d-md-none">
            {filteredLocations.map((loc) => (
              <li
                key={loc.location_id}
                className="list-group-item d-flex flex-column"
              >
                <div>
                  <strong>Name:</strong> {loc.location_name}
                </div>
                <div>
                  <strong>Entity Type:</strong> {loc.entity_type}
                </div>
                <div className="mt-2 d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => onEdit(loc)}
                    aria-label={`Edit ${loc.location_name}`}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(loc.location_id)}
                    aria-label={`Delete ${loc.location_name}`}
                  >
                    🗑 Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default LocationList;
